import { z } from 'zod'
import { OptionalString } from '@/shared/api'

/**
 * Согласие — журнал, а не флаг: 152-ФЗ ст. 9 требует доказуемости, поэтому
 * хранится дословный текст, который человек видел, и обе отметки — выдача и
 * отзыв. Записи журнала не переписываются.
 */
export const ConsentSchema = z
  .object({
    id: z.uuid(),
    purpose: z.string(),
    wording: z.string(),
    source: z.string(),
    active: z.boolean(),
    granted_at: z.iso.datetime(),
    revoked_at: z.iso.datetime().nullish(),
  })
  .transform((dto) => ({
    id: dto.id,
    purpose: dto.purpose,
    wording: dto.wording,
    source: dto.source,
    active: dto.active,
    grantedAt: dto.granted_at,
    revokedAt: dto.revoked_at ?? null,
  }))

export type Consent = z.infer<typeof ConsentSchema>

export const VisitSchema = z
  .object({
    excursion_id: z.uuid(),
    title: z.string(),
    state: z.string(),
    held_at: z.iso.datetime(),
  })
  .transform((dto) => ({
    excursionId: dto.excursion_id,
    excursionTitle: dto.title,
    state: dto.state,
    heldAt: dto.held_at,
  }))

export type Visit = z.infer<typeof VisitSchema>

/**
 * Клиент. Телефон НЕ уникален принципиально: муж и жена на одном номере — два
 * разных контакта. Именно запрет на это заказчик назвал причиной отказа от
 * готовой CRM. Совпадение ищется по паре имя+телефон (`ClientMatching`).
 */
const ClientBodySchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string(),
    note: z.string().optional().default(''),
    /** Стирание — обезличивание, строка остаётся: прошлые записи не осиротеют. */
    forgotten: z.boolean(),
    merged_into: OptionalString,
    consents: z.array(ConsentSchema).default([]),
  })
  .transform((dto) => ({
    id: dto.id,
    name: dto.name,
    phone: dto.phone,
    note: dto.note,
    forgotten: dto.forgotten,
    mergedInto: dto.merged_into,
    consents: dto.consents,
  }))

/**
 * Карточка контакта: сам контакт, история и подсказки о дублях. Дубли —
 * подсказка команде (телефон совпал, имя отличается), а не автоматика:
 * объединять решает человек.
 */
export const ClientCardSchema = z
  .object({
    client: ClientBodySchema,
    visits: z.array(VisitSchema).default([]),
    duplicate_suspects: z.array(z.object({ client_id: z.uuid(), name: z.string() })).default([]),
  })
  .transform((dto) => ({
    ...dto.client,
    visits: dto.visits,
    duplicateSuspects: dto.duplicate_suspects.map((d) => ({
      clientId: d.client_id,
      name: d.name,
    })),
  }))

export type Client = z.infer<typeof ClientCardSchema>

export const ClientListSchema = z.array(ClientCardSchema)

/** Ручки правки отдают голый контакт — карточку добираем перезапросом списка. */
export const ClientSchema = ClientBodySchema

/**
 * Ответ заведения контакта. `matched` — контакт подобрался по паре имя+телефон,
 * нового не завели: повтор того же человека не превращается в дубль.
 */
export const ClientRegistrationSchema = z.object({
  client: ClientBodySchema,
  matched: z.boolean(),
})

export type ClientRegistration = z.infer<typeof ClientRegistrationSchema>

export const RevokeResultSchema = z
  .object({
    client: ClientBodySchema,
    /** Отзыв согласия не отменяет будущие записи — это разговор, а не автоматика. */
    has_active_bookings: z.boolean(),
    needs_review: z.boolean().default(false),
  })
  .transform((dto) => ({
    hasActiveBookings: dto.has_active_bookings,
    needsReview: dto.needs_review,
  }))

export type RevokeResult = z.infer<typeof RevokeResultSchema>

export interface ClientPatch {
  name?: string
  phone?: string
  note?: string
}

export interface ClientDraft {
  name: string
  phone: string
  note: string
}

export function activeConsent(client: Client): Consent | undefined {
  return client.consents.find((c) => c.active)
}

/** `+7 (999) 123-45-67` — телефон читается глазами, а не парсится. */
export function fmtPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length !== 11) return raw
  return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
}
